import { useEffect, useRef, useState } from "react"
import { loadAudioBuffer, playOneShot, setMetronomePan, getCurrentTime, startAudioEngine, stopMetronomeSounds } from "../audioManager"
import { useStorePads } from "../store"

export default function useMetronome() {
    const bpm = useStorePads((state) => state.metronomeBpm)
    const setBpm = useStorePads((state) => state.setMetronomeBpm)
    const measures = useStorePads((state) => state.metronomeMeasures)
    const setMeasures = useStorePads((state) => state.setMetronomeMeasures)
    const playback = useStorePads((state) => state.playback)
    const pan = useStorePads((state) => state.metronomePan)
    const setPan = useStorePads((state) => state.setMetronomePan)
    const isRunning = useStorePads((state) => state.isMetronomeRunning)
    const setIsRunning = useStorePads((state) => state.setMetronomeRunning)

    const beat1 = useRef(null)
    const beat2 = useRef(null)
    const beatsLoadingRef = useRef(null)
    const schedulerTimeout = useRef(null)
    const uiTimeoutsRef = useRef([])
    const nextNoteTimeRef = useRef(0)
    const scheduledCountRef = useRef(1)

    const measureCount = useRef(measures)

    const [count, setCount] = useState(1)
    const counter = useRef(1)

    const taps = useRef([])

    const ensureBeatsLoaded = async () => {
        if (beat1.current && beat2.current) return
        if (!beatsLoadingRef.current) {
            beatsLoadingRef.current = (async () => {
            beat1.current = await loadAudioBuffer('/metronome/1.mp3')
            beat2.current = await loadAudioBuffer('/metronome/2.mp3')
            })().finally(() => {
                beatsLoadingRef.current = null
            })
        }

        await beatsLoadingRef.current
    }

    useEffect(() => {
        setMetronomePan(pan)
    }, [pan])

    useEffect(() => {
        return () => {
            if (schedulerTimeout.current) {
                clearTimeout(schedulerTimeout.current)
            }
            uiTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
            uiTimeoutsRef.current = []
            setIsRunning(() => false)
            setCount(() => 1)
            counter.current = 1
        }
    }, [setIsRunning])

    useEffect(() => {
        measureCount.current = measures
        scheduledCountRef.current = 1
        setCount(1)
        counter.current = 1
    }, [measures])

    const playbackSyncKey =
        playback?.source === 'setlist'
            ? `${playback.title ?? ''}|${playback.library ?? ''}|${playback.note ?? ''}`
            : ''

    useEffect(() => {
        const clearScheduler = () => {
            if (schedulerTimeout.current) {
                clearTimeout(schedulerTimeout.current)
                schedulerTimeout.current = null
            }
            uiTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
            uiTimeoutsRef.current = []
            stopMetronomeSounds()
        }

        if (isRunning) {
            const lookaheadMs = 25
            const scheduleAheadSeconds = 0.12
            const secondsPerBeat = () => 60 / bpm

            counter.current = 1
            scheduledCountRef.current = 1
            setCount(1)
            nextNoteTimeRef.current = getCurrentTime() + 0.02

            const scheduler = () => {
                const now = getCurrentTime()

                while (nextNoteTimeRef.current < now + scheduleAheadSeconds) {
                    const currentMeasure = scheduledCountRef.current
                    const isAccent = currentMeasure === 1

                    if (isAccent && beat1.current) {
                        void playOneShot(beat1.current, { when: nextNoteTimeRef.current })
                    } else if (beat2.current) {
                        void playOneShot(beat2.current, { when: nextNoteTimeRef.current })
                    }

                    const uiCount = currentMeasure
                    const delayMs = Math.max(0, (nextNoteTimeRef.current - now) * 1000)
                    const timeoutId = setTimeout(() => {
                        setCount(uiCount)
                        uiTimeoutsRef.current = uiTimeoutsRef.current.filter((id) => id !== timeoutId)
                    }, delayMs)
                    uiTimeoutsRef.current.push(timeoutId)

                    scheduledCountRef.current = (currentMeasure % measureCount.current) + 1
                    counter.current = scheduledCountRef.current
                    nextNoteTimeRef.current += secondsPerBeat()
                }

                schedulerTimeout.current = setTimeout(scheduler, lookaheadMs)
            }

            scheduler()
            return clearScheduler
        }

        clearScheduler()
        setCount(1)
        counter.current = 1
        scheduledCountRef.current = 1
    }, [bpm, isRunning, playbackSyncKey])

    const play = async () => {
        await startAudioEngine()
        await ensureBeatsLoaded()
        setIsRunning(true)
    }

    const stop = () => {
        setIsRunning(false)
    }

    const calculateBPM = () => {
        const timeDifferences = []
        const millisecondsInMinute = 60000

        if (taps.current.length < 2) return 0

        for (let i = 1; i < taps.current.length; i++) {
            const diff = taps.current[i] - taps.current[i - 1]
            timeDifferences.push(diff)
        }

        const averageTimeDiff = timeDifferences.reduce((acc, val) => {
            return acc + val
        }, 0)

        const bpm = Math.round(
            millisecondsInMinute /
            (averageTimeDiff / timeDifferences.length)
        )

        return bpm
    }

    async function tap() {
        await startAudioEngine()
        await ensureBeatsLoaded()
        const timestamp = Date.now()
        taps.current = [...taps.current.slice(-4), timestamp]

        const bpm = calculateBPM()

        if (bpm > 25 && bpm < 300) {
            setBpm(bpm)
            setBpmInterval(bpm)
        }
    }

    const setBpmInterval = (bpmValue) => {
        setBpm(bpmValue)
    }

    const setMeasureCount = (measureLength) => {
        setMeasures(measureLength)
    }

    return {
        bpm,
        tap,
        play,
        stop,
        isRunning,
        measures,
        pan,
        count,
        setMeasureCount,
        setInterval: setBpmInterval,
        setPan
    }
}
